import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Icon } from './Icon.tsx';

const pad = (n: number) => String(n).padStart(2, '0');
const hm = (m: number) => `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;
const mins = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));

// Seletor de horário: um botão com o horário escolhido que abre uma lista rolável de 15 em 15 minutos.
// Os horários livres aparecem como "livre". Com `allowBusy`, dá para escolher também os ocupados (sobreposição liberada).
export function TimeSelect({ label, value, onChange, free, from, to, step = 15, allowBusy }: {
  label: string; value: string | null; onChange: (t: string) => void; free: string[]; from: string; to: string; step?: number; allowBusy: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const root = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const id = useId();
  const freeSet = useMemo(() => new Set(free), [free]);
  const options = useMemo(() => { const out: string[] = []; for (let m = Math.ceil(mins(from) / step) * step; m <= mins(to); m += step) out.push(hm(m)); return out; }, [from, to, step]);
  const enabled = (t: string) => allowBusy || freeSet.has(t);

  const center = (i: number) => {
    const ul = list.current, li = ul?.children[i] as HTMLElement | undefined;
    if (ul && li) ul.scrollTop = li.offsetTop - ul.clientHeight / 2 + li.clientHeight / 2;
  };
  // abre já posicionado no horário escolhido ou no primeiro livre
  useEffect(() => {
    if (!open) return;
    const start = value ?? options.find((t) => freeSet.has(t)) ?? options[0] ?? '';
    const i = Math.max(0, options.indexOf(start));
    setActive(i);
    requestAnimationFrame(() => center(i));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const outside = (e: Event) => { if (!root.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', outside);
    document.addEventListener('touchstart', outside);
    return () => { document.removeEventListener('mousedown', outside); document.removeEventListener('touchstart', outside); };
  }, [open]);

  const pick = (t: string) => { if (!enabled(t)) return; onChange(t); setOpen(false); };
  const move = (d: number) => {
    let i = active;
    do { i += d; } while (i > 0 && i < options.length - 1 && !enabled(options[i]!));
    i = Math.min(options.length - 1, Math.max(0, i));
    setActive(i);
    const ul = list.current, li = ul?.children[i] as HTMLElement | undefined;
    if (ul && li) { if (li.offsetTop < ul.scrollTop) ul.scrollTop = li.offsetTop; else if (li.offsetTop + li.clientHeight > ul.scrollTop + ul.clientHeight) ul.scrollTop = li.offsetTop + li.clientHeight - ul.clientHeight; }
  };
  const key = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); if (!open) setOpen(true); else move(e.key === 'ArrowDown' ? 1 : -1); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (!open) setOpen(true); else pick(options[active]!); }
    else if (e.key === 'Escape' && open) { e.preventDefault(); setOpen(false); }
    else if (e.key === 'Home' && open) { e.preventDefault(); setActive(0); center(0); }
    else if (e.key === 'End' && open) { e.preventDefault(); setActive(options.length - 1); center(options.length - 1); }
    else if (e.key === 'Tab') setOpen(false);
  };

  return (
    <div className="tsel" ref={root}>
      <button type="button" className={'tsel-btn' + (value ? '' : ' empty') + (open ? ' open' : '')} role="combobox" aria-haspopup="listbox" aria-expanded={open}
        aria-controls={id} aria-label={label} onClick={() => setOpen(!open)} onKeyDown={key}>
        <span>{value ?? 'Escolha o horário'}</span>
        {value && <small>{freeSet.has(value) ? 'livre' : 'já tem alguém'}</small>}
        <Icon name="chev" />
      </button>
      {open && (
        <ul id={id} ref={list} role="listbox" className="tsel-list" aria-label={label}>
          {options.map((t, i) => (
            <li key={t} role="option" aria-selected={t === value} aria-disabled={!enabled(t)} className={i === active ? 'active' : ''}
              onMouseEnter={() => setActive(i)} onClick={() => pick(t)}>
              <span>{t}</span>
              <small>{freeSet.has(t) ? 'livre' : allowBusy ? 'ocupado' : 'indisponível'}</small>
              {t === value && <Icon name="check" />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
